import { SpinLoader } from '@/components/loader/spin-loader';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { type JSX } from 'react';
import { ClipboardCopy } from '@/components/home/clipboard-copy';
import { AlertTriangle, Slash } from 'react-feather';
import { Button } from '@/components/home/buttons';
import type { GameState } from '@/game/core/game';
import { Card } from '@/components/home/card';

interface MessageView {
    icon: JSX.Element;
    text: string;
    showUrlCopy?: boolean;
    showHomeButton?: boolean;
}

interface GameMessageProps {
    state: GameState;
}

export function GameMessage({ state }: GameMessageProps) {
    const gameId = useParams<{ gameId: string }>().gameId || '';
    const { t } = useTranslation();

    const viewMap: Record<GameState, MessageView | undefined> = {
        spectate: undefined,
        ended: undefined,
        configure: undefined,
        lobby: undefined,
        nosuchgame: {
            icon: <Slash className="w-6 h-6" />,
            text: t('game.message.noSuchGame'),
            showHomeButton: true,
        },
        singleplayer: undefined,
        multiplayer: undefined,
        loading: {
            icon: <SpinLoader className="w-6 h-6" />,
            text: t('game.message.txtLoading'),
        },
        error: {
            icon: <AlertTriangle className="w-6 h-6" />,
            text: t('game.message.txtCannotJoin'),
            showHomeButton: true,
        },
    };

    const fullUrl = `${window.location.origin}/join/${gameId}`;
    const view = viewMap[state];

    return (
        <div className="w-full h-full flex items-center justify-center">
            <Card className="relative w-full max-w-md shadow-xl">
                <div className="flex flex-col items-center p-4 md:p-6">
                    <div className="">
                        {view?.icon}
                    </div>
                    <h3 className="mt-4 md:text-base font-normal">{view?.text}</h3>
                    {view?.showUrlCopy && fullUrl &&
                        <>
                            <ClipboardCopy className="my-3" text={fullUrl} displayText={gameId} />
                            <h3 className="md:text-base font-normal">{t('game.lobby.txtShareInvite')}</h3>
                        </>
                    }
                    {view?.showHomeButton &&
                        <Link to="/create" className="w-full mt-5">
                            <Button className="w-full">{t('home.btnNewGame')}</Button>
                        </Link>
                    }
                </div>
            </Card>
        </div>
    );
}
